import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { getApiInstance } from "../utils/api";
import { setSimboloActivo } from "../utils/moneda";

/**
 * Moneda en la que se muestran los importes de los informes.
 *
 * Es global: se elige una vez en la cabecera y la respetan todas las pantallas,
 * para que nadie compare un informe en euros con otro en dólares sin darse cuenta.
 * La conversión la hace el backend; aquí solo se guarda qué se ha elegido.
 */

const MonedaContext = createContext();

const LOCAL_STORAGE_KEY = "app_moneda_informes";

/* Respaldo por si la llamada falla: son las tres del selector y no cambian.
   El backend manda esta misma lista con MonedasZona.Seleccionables. */
const MONEDAS_POR_DEFECTO = [
    { codigo: "EUR", nombre: "Euro", simbolo: "€" },
    { codigo: "USD", nombre: "Dólar estadounidense", simbolo: "US$" },
    { codigo: "MXN", nombre: "Peso mexicano", simbolo: "MX$" },
];

const leerGuardada = () => {
    try {
        return window.localStorage.getItem(LOCAL_STORAGE_KEY);
    } catch (error) {
        console.error("Error al leer de localStorage", error);
        return null;
    }
};

export const MonedaProvider = ({ children }) => {
    const [monedas, setMonedas] = useState(MONEDAS_POR_DEFECTO);
    const [moneda, setMonedaState] = useState(() => leerGuardada());
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        const api = getApiInstance();

        api.get("/api/Monedas/GetMonedas")
            .then(({ data }) => {
                const disponibles = data?.monedas?.length
                    ? data.monedas
                    : MONEDAS_POR_DEFECTO;

                setMonedas(disponibles);

                /* La guardada manda, pero solo si sigue siendo una moneda válida.
                   Si no hay ninguna, la que corresponde a la zona del usuario. */
                setMonedaState((actual) => {
                    const valida = disponibles.some((m) => m.codigo === actual);
                    return valida ? actual : data?.porDefecto ?? "EUR";
                });
            })
            .catch((error) => {
                console.error("Error al cargar las monedas", error);
                setMonedaState((actual) => actual ?? "EUR");
            })
            .finally(() => setCargando(false));
    }, []);

    const setMoneda = useCallback((codigo) => {
        setMonedaState(codigo);
        try {
            window.localStorage.setItem(LOCAL_STORAGE_KEY, codigo);
        } catch (error) {
            console.error("Error al guardar en localStorage", error);
        }
    }, []);

    /* Lo dejan disponible para las funciones de formato de los informes
       personalizados, que no son componentes y no pueden leer el contexto. */
    useEffect(() => {
        const actual = monedas.find((m) => m.codigo === moneda);
        setSimboloActivo(actual?.simbolo);
    }, [moneda, monedas]);

    const value = useMemo(() => {
        const actual = monedas.find((m) => m.codigo === moneda);

        return {
            moneda,
            setMoneda,
            monedas,
            simbolo: actual?.simbolo ?? "€",
            nombreMoneda: actual?.nombre ?? "",
            cargandoMoneda: cargando,
        };
    }, [moneda, monedas, cargando, setMoneda]);

    return (
        <MonedaContext.Provider value={value}>{children}</MonedaContext.Provider>
    );
};

export const useMoneda = () => {
    const context = useContext(MonedaContext);
    if (context === undefined) {
        throw new Error("useMoneda debe usarse dentro de un MonedaProvider");
    }
    return context;
};
